import type { RuleGroupType } from 'react-querybuilder';
import type { StateCreator } from 'zustand';
import { selectionBlocker, toAdAccountOptions } from '../lib/adAccountEligibility';
import { type AudienceFieldName, audienceField, isOfferedField } from '../lib/audienceFields';
import { DEFAULT_BRAND_SHARE_PERCENT, isValidBrandShare } from '../lib/brandShare';
import { buildCreateRequest } from '../lib/buildCreateRequest';
import { PROMPT_MAX_LENGTH } from '../lib/constants';
import { currentUserId } from '../lib/currentUserId';
import { EMPTY_FILTERS, pickFilters, toRequestFilters } from '../lib/filters';
import { hasFilterErrors, validateFilters } from '../lib/filterValidation';
import { type LoadError, loadErrorMessage, toLoadError } from '../lib/loadErrorMessage';
import { logError } from '../lib/logError';
import {
  addFieldRule,
  blockingDropped,
  EMPTY_TREE,
  fieldsInTree,
  flatToTree,
  patchTree,
  removeFieldRule,
  treeToFlat
} from '../lib/queryMapping';
import { isUnconfirmedCreateMatch } from '../lib/unconfirmedCreateMatch';
import type {
  ApiResult,
  AudienceDefinition,
  AudienceDefinitionPreviewResponse,
  AudienceDeletePreview,
  AudienceDeleteResult,
  AudienceFilters,
  AudienceSnapshot,
  AudienceStatus,
  CreateAudienceRequest,
  CustomAudiencesApiContext,
  CustomAudiencesApiError,
  Paginated,
  UpdateAudienceRequest
} from '../types';
import { type LoadGuard, mayWrite } from './loadGuard';
import type { CustomAudiencesStore } from './useCustomAudiencesStore';
import type { PreviewState, UnconfirmedCreate, WizardState } from './wizardTypes';

export const DEFINITIONS_PAGE_SIZE = 25;
export const SNAPSHOTS_PAGE_SIZE = 10;

type LoadStatus = 'idle' | 'loading' | 'loaded' | 'error';

/** What `submitCreate` did, so the container can pick the toast and whether to navigate. */
export type CreateOutcome =
  /* `adAccountCount` is how many ad accounts the create delivers to */
  | { kind: 'created'; definition: AudienceDefinition; adAccountCount: number }
  /* 409: the service kept the definition but could not start its first snapshot */
  | { kind: 'failedAfterCreate'; message: string }
  /* 500, 502, 504, or no response: the rows may or may not exist, so the draft stays and `wizard.unconfirmedCreate` holds what the list showed */
  | { kind: 'unconfirmed'; message: string }
  | { kind: 'invalid'; message: string }
  /* Anything else: nothing was saved, so the draft stays for another try */
  | { kind: 'failed'; message: string }
  | { kind: 'skipped' };

/* Statuses a create can end with after the service may already have written the rows; null is no response, such as a timeout */
const UNCONFIRMED_CREATE_STATUSES: (number | null)[] = [500, 502, 504, null];

/* Of those, the ones where the service may still be working when the create returns, so its row can land after the list check */
const UNANSWERED_CREATE_STATUSES: (number | null)[] = [502, 504, null];

/**
 * The delete dialog's preview of one definition.
 *
 * - `loading`: the preview request is in flight.
 * - `loaded`: `preview` is what the delete would do.
 * - `error`: the preview failed, and `message` follows a "Couldn't load X:" style prefix.
 */
export type DeletePreviewState =
  | { definitionId: number; status: 'loading' }
  | { definitionId: number; status: 'loaded'; preview: AudienceDeletePreview }
  | { definitionId: number; status: 'error'; message: string };

export const UNCONFIRMED_CREATE_BLOCKER =
  'Check the audience that looks saved, or create it anyway';

/* Everything a brand switch clears */
const EMPTY_DEFINITIONS_DATA = {
  definitions: [] as AudienceDefinition[],
  definitionsTotal: 0,
  definitionsPageNumber: 1,
  definitionsStatus: 'idle' as LoadStatus,
  definitionsError: null as string | null,
  definitionsErrorStatus: null as number | null,
  latestSnapshotByDefinitionId: {} as Record<number, AudienceSnapshot | null>,
  latestSnapshotErrorByDefinitionId: {} as Record<number, LoadError>,
  selectedDefinition: null as AudienceDefinition | null,
  selectedDefinitionStatus: 'idle' as LoadStatus,
  selectedDefinitionError: null as string | null,
  snapshots: [] as AudienceSnapshot[],
  snapshotsTotal: 0,
  snapshotsPageNumber: 1,
  snapshotsError: null as string | null,
  deletePreview: null as DeletePreviewState | null
};

export type CustomAudiencesSlice = {
  /** The brand the loaded data belongs to; a load for another brand clears it first. */
  loadedBrandId: string | null;
  definitions: AudienceDefinition[];
  definitionsTotal: number;
  definitionsPageNumber: number;
  definitionsStatus: LoadStatus;
  definitionsError: string | null;
  /** The failed list load's HTTP status, null when no response arrived or the load did not fail. */
  definitionsErrorStatus: number | null;
  latestSnapshotByDefinitionId: Record<number, AudienceSnapshot | null>;
  /** Why a definition's latest run could not be loaded; its row reads Unknown. */
  latestSnapshotErrorByDefinitionId: Record<number, LoadError>;
  updatingDefinitionId: number | null;

  selectedDefinition: AudienceDefinition | null;
  selectedDefinitionStatus: LoadStatus;
  selectedDefinitionError: string | null;
  snapshots: AudienceSnapshot[];
  snapshotsTotal: number;
  snapshotsPageNumber: number;
  snapshotsError: string | null;
  /** The delete dialog's preview, or null while the dialog is closed. */
  deletePreview: DeletePreviewState | null;

  wizard: WizardState;

  loadDefinitions: (
    ctx: CustomAudiencesApiContext,
    options?: { pageNumber?: number }
  ) => Promise<void>;
  /** PATCHes name, description, or status and swaps the row in place. */
  updateDefinitionFields: (
    ctx: CustomAudiencesApiContext,
    definitionId: number,
    body: UpdateAudienceRequest
  ) => Promise<ApiResult<AudienceDefinition>>;
  setDefinitionStatus: (
    ctx: CustomAudiencesApiContext,
    definitionId: number,
    status: Exclude<AudienceStatus, 'deleted'>
  ) => Promise<ApiResult<AudienceDefinition>>;
  /**
   * Loads what deleting a definition would do into `deletePreview`.
   *
   * A 403 names the missing grant.
   * The write is dropped for another brand, or once a later load or `clearDeletePreview` superseded it.
   */
  loadDeletePreview: (ctx: CustomAudiencesApiContext, definitionId: number) => Promise<void>;
  clearDeletePreview: () => void;
  /**
   * Deletes a definition and its audiences on Meta, then quietly reloads the selected definition's details.
   *
   * - On data, the returned definition replaces the row.
   * - The details reload on an error too, since a delete that failed partway or timed out may have removed some audiences.
   * - `updatingDefinitionId` holds the definition until the reload finishes.
   */
  deleteDefinition: (
    ctx: CustomAudiencesApiContext,
    definitionId: number
  ) => Promise<ApiResult<AudienceDeleteResult>>;
  /** Loads a definition, its snapshot page, and its destinations; `refresh` is the quiet poll. */
  loadDefinitionDetails: (
    ctx: CustomAudiencesApiContext,
    definitionId: number,
    options?: { refresh?: boolean }
  ) => Promise<void>;
  /** Loads a snapshot page of the selected definition; the write is dropped for another brand or a guard that is no longer current. */
  loadSnapshots: (
    ctx: CustomAudiencesApiContext,
    definitionId: number,
    pageNumber: number,
    guard?: LoadGuard
  ) => Promise<ApiResult<Paginated<AudienceSnapshot>>>;

  setPrompt: (prompt: string) => void;
  submitPreview: (ctx: CustomAudiencesApiContext) => Promise<void>;
  /** Loads a `definition` preview into the rules, with its name and description. */
  acceptPreview: () => void;
  setWizardName: (name: string) => void;
  setWizardDescription: (description: string) => void;
  /** Replaces the rule tree; the flat filters and dropped list follow from it. */
  setWizardQuery: (query: RuleGroupType) => void;
  /**
   * Adds a rule for the field, or removes it when one exists (the column mapper's checkbox).
   *
   * A field that is not offered is never added.
   */
  toggleWizardField: (name: AudienceFieldName) => void;
  /** Applies a flat-filter patch, such as a chip's clear patch, to the rule tree. */
  updateWizardFilters: (patch: Partial<AudienceFilters>) => void;
  /** Sets the brand share; a change marks the count stale. */
  setWizardBrandSharePercent: (brandSharePercent: number | null) => void;
  requestCount: (ctx: CustomAudiencesApiContext) => Promise<void>;
  /** Moves to the Deliver step when `selectCreateBlocker` allows it; returns whether it moved. */
  goToDeliverStep: () => boolean;
  /** Returns to the Define step with the draft and the Deliver-to selection intact. */
  goToDefineStep: () => void;
  /**
   * Creates the definition and delivers it to the selected targets in one call.
   *
   * An invalid definition or selection returns `invalid` with the blocker and never calls the API.
   * So does an earlier unconfirmed create that found or could not check a saved row, unless `createAnyway` is set.
   */
  submitCreate: (
    ctx: CustomAudiencesApiContext,
    options?: { createAnyway?: boolean }
  ) => Promise<CreateOutcome>;
  /** Starts the wizard as a copy of a saved definition: same rules and brand share, name suffixed " (copy)". */
  prefillWizardFromDefinition: (definition: AudienceDefinition) => void;
  /** Loads a definition (from the store when cached) and prefills the wizard with a copy of it. */
  startCloneOf: (
    ctx: CustomAudiencesApiContext,
    definitionId: number
  ) => Promise<ApiResult<AudienceDefinition>>;
  /** Clears the draft and the Deliver-to selection. */
  resetWizard: () => void;
};

export const INITIAL_WIZARD: WizardState = {
  step: 'define',
  prompt: '',
  previewedPrompt: null,
  preview: { kind: 'idle' },
  acceptedPreview: null,
  clonedNlPrompt: null,
  name: '',
  description: '',
  query: EMPTY_TREE,
  filters: EMPTY_FILTERS,
  dropped: [],
  proposedValues: {},
  brandSharePercent: DEFAULT_BRAND_SHARE_PERCENT,
  filtersRevision: 0,
  count: { kind: 'idle' },
  submitting: false,
  createErrors: [],
  unconfirmedCreate: null
};

const UNEXPECTED_LOAD_MESSAGE = 'Something went wrong loading audiences. Try again.';

export const createCustomAudiencesSlice: StateCreator<
  CustomAudiencesStore,
  [],
  [],
  CustomAudiencesSlice
> = (set, get) => {
  /* Each load bumps its counter; a response from a superseded load is dropped */
  let definitionsRequestId = 0;
  let detailsRequestId = 0;
  let deletePreviewRequestId = 0;

  const setWizard = (patch: Partial<WizardState>) =>
    set(state => ({ wizard: { ...state.wizard, ...patch } }));

  /* The count goes stale only when the flat filters change, not when an empty rule is added */
  const setQuery = (query: RuleGroupType, extra: Partial<WizardState> = {}) => {
    const { filters, dropped, proposedValues } = treeToFlat(query);
    const current = get().wizard;
    const changed = JSON.stringify(filters) !== JSON.stringify(current.filters);
    setWizard({
      ...extra,
      query,
      filters,
      dropped,
      proposedValues,
      filtersRevision: changed ? current.filtersRevision + 1 : current.filtersRevision
    });
  };

  /* Swaps a written definition into the list and the selection */
  const replaceDefinition = (updated: AudienceDefinition) =>
    set(state => ({
      definitions: state.definitions.map(definition =>
        definition.id === updated.id ? updated : definition
      ),
      selectedDefinition:
        state.selectedDefinition?.id === updated.id ? updated : state.selectedDefinition
    }));

  /* Data belongs to one brand; a load for another brand starts from empty */
  const syncBrand = (ctx: CustomAudiencesApiContext) => {
    if (get().loadedBrandId === ctx.brandId) return;
    set({ ...EMPTY_DEFINITIONS_DATA, loadedBrandId: ctx.brandId });
    get().clearDestinationsData();
  };

  const loadLatestSnapshot = async (ctx: CustomAudiencesApiContext, definitionId: number) => {
    const result = await ctx.api.listSnapshots(ctx, {
      definitionId,
      pageSize: 1,
      pageNumber: 1
    });
    return result;
  };

  /* The status pill reads the latest run, which a page other than the first does not carry */
  const refreshLatestSnapshot = async (
    ctx: CustomAudiencesApiContext,
    definitionId: number,
    guard: LoadGuard
  ) => {
    const result = await loadLatestSnapshot(ctx, definitionId);
    if (!result.data || !mayWrite(get().loadedBrandId, ctx, guard)) return;
    const latest = result.data.data[0] ?? null;
    set(state => ({
      latestSnapshotByDefinitionId: {
        ...state.latestSnapshotByDefinitionId,
        [definitionId]: latest
      }
    }));
    if (latest) await get().loadDeliveries(ctx, [latest.id], guard);
  };

  return {
    ...EMPTY_DEFINITIONS_DATA,
    loadedBrandId: null,
    updatingDefinitionId: null,

    wizard: INITIAL_WIZARD,

    loadDefinitions: async (ctx, options = {}) => {
      syncBrand(ctx);
      const requestId = ++definitionsRequestId;
      const isCurrent = () =>
        requestId === definitionsRequestId &&
        get().loadedBrandId === ctx.brandId &&
        !ctx.signal?.aborted;
      const guard: LoadGuard = { isCurrent };
      const pageNumber = options.pageNumber ?? get().definitionsPageNumber;
      if (get().definitionsStatus === 'idle') set({ definitionsStatus: 'loading' });

      try {
        const result = await ctx.api.listDefinitions(ctx, {
          pageSize: DEFINITIONS_PAGE_SIZE,
          pageNumber
        });
        if (!isCurrent()) return;
        if (result.error) {
          set({
            definitionsStatus: 'error',
            definitionsError: result.error.message,
            definitionsErrorStatus: result.error.status
          });
          return;
        }
        if (!Array.isArray(result.data?.data))
          throw new Error('listDefinitions returned no data array');

        const definitionIds = result.data.data.map(definition => definition.id);
        const snapshotResults = await Promise.all(
          definitionIds.map(definitionId => loadLatestSnapshot(ctx, definitionId))
        );
        /* A failed lookup keeps the previous run and records why, so the row reads Unknown */
        const previous = get().latestSnapshotByDefinitionId;
        const latestSnapshotByDefinitionId: Record<number, AudienceSnapshot | null> = {};
        const latestSnapshotErrorByDefinitionId: Record<number, LoadError> = {};
        definitionIds.forEach((definitionId, index) => {
          const snapshotResult = snapshotResults[index];
          if (snapshotResult.data) {
            latestSnapshotByDefinitionId[definitionId] = snapshotResult.data.data[0] ?? null;
            return;
          }
          if (definitionId in previous)
            latestSnapshotByDefinitionId[definitionId] = previous[definitionId];
          latestSnapshotErrorByDefinitionId[definitionId] = toLoadError(
            snapshotResult.error,
            'snapshots'
          );
          logError('load.latestSnapshot', { definitionId, error: snapshotResult.error });
        });

        await Promise.all([
          get().loadDestinations(ctx, definitionIds, guard),
          get().loadDeliveries(ctx, latestSnapshotIds(latestSnapshotByDefinitionId), guard)
        ]);
        if (!isCurrent()) return;

        set({
          definitions: result.data.data,
          definitionsTotal: result.data.total,
          definitionsPageNumber: pageNumber,
          definitionsStatus: 'loaded',
          definitionsError: null,
          definitionsErrorStatus: null,
          latestSnapshotByDefinitionId,
          latestSnapshotErrorByDefinitionId
        });
      } catch (error) {
        logError('load.definitions', error);
        if (isCurrent())
          set({
            definitionsStatus: 'error',
            definitionsError: UNEXPECTED_LOAD_MESSAGE,
            definitionsErrorStatus: null
          });
      }
    },

    updateDefinitionFields: async (ctx, definitionId, body) => {
      set({ updatingDefinitionId: definitionId });
      const result = await ctx.api.updateDefinition(ctx, definitionId, body);
      if (result.data) replaceDefinition(result.data);
      set({ updatingDefinitionId: null });
      return result;
    },

    setDefinitionStatus: (ctx, definitionId, status) =>
      get().updateDefinitionFields(ctx, definitionId, { status }),

    loadDeletePreview: async (ctx, definitionId) => {
      syncBrand(ctx);
      const requestId = ++deletePreviewRequestId;
      set({ deletePreview: { definitionId, status: 'loading' } });
      const result = await ctx.api.getDeletePreview(ctx, definitionId);
      if (requestId !== deletePreviewRequestId || !mayWrite(get().loadedBrandId, ctx)) return;
      if (result.error) {
        logError('load.deletePreview', { definitionId, error: result.error });
        set({
          deletePreview: {
            definitionId,
            status: 'error',
            message: loadErrorMessage(result.error, 'deletePreview')
          }
        });
        return;
      }
      set({ deletePreview: { definitionId, status: 'loaded', preview: result.data } });
    },

    clearDeletePreview: () => {
      deletePreviewRequestId += 1;
      set({ deletePreview: null });
    },

    deleteDefinition: async (ctx, definitionId) => {
      set({ updatingDefinitionId: definitionId });
      const result = await ctx.api.deleteDefinition(ctx, definitionId);
      if (result.data) replaceDefinition(result.data.definition);
      if (get().selectedDefinition?.id === definitionId)
        await get().loadDefinitionDetails(ctx, definitionId, { refresh: true });
      set({ updatingDefinitionId: null });
      return result;
    },

    loadDefinitionDetails: async (ctx, definitionId, options = {}) => {
      syncBrand(ctx);
      const requestId = ++detailsRequestId;
      const isCurrent = () =>
        requestId === detailsRequestId &&
        get().loadedBrandId === ctx.brandId &&
        !ctx.signal?.aborted;
      const guard: LoadGuard = { isCurrent };
      const current =
        get().selectedDefinition?.id === definitionId ? get().selectedDefinition : null;
      /* A poll refreshes in place: no loading state, same snapshot page, and a failure keeps the page */
      const refresh = Boolean(options.refresh && current);
      if (!refresh)
        set({
          selectedDefinitionStatus: 'loading',
          selectedDefinitionError: null,
          selectedDefinition: current
        });

      try {
        const result = await ctx.api.getDefinition(ctx, definitionId);
        if (!isCurrent()) return;
        if (result.error) {
          if (refresh) logError('refresh.definition', { definitionId, error: result.error });
          else
            set({
              selectedDefinitionStatus: 'error',
              selectedDefinitionError: result.error.message
            });
          return;
        }
        set({ selectedDefinition: result.data });

        const pageNumber = refresh ? get().snapshotsPageNumber : 1;
        await Promise.all([
          get().loadSnapshots(ctx, definitionId, pageNumber, guard),
          pageNumber === 1 ? Promise.resolve() : refreshLatestSnapshot(ctx, definitionId, guard),
          get().loadDestinations(ctx, [definitionId], guard)
        ]);
        if (!isCurrent()) return;
        set({ selectedDefinitionStatus: 'loaded' });
      } catch (error) {
        logError('load.definitionDetails', { definitionId, error });
        if (isCurrent() && !refresh)
          set({
            selectedDefinitionStatus: 'error',
            selectedDefinitionError: UNEXPECTED_LOAD_MESSAGE
          });
      }
    },

    loadSnapshots: async (ctx, definitionId, pageNumber, guard) => {
      const result = await ctx.api.listSnapshots(ctx, {
        definitionId,
        pageSize: SNAPSHOTS_PAGE_SIZE,
        pageNumber
      });
      if (!mayWrite(get().loadedBrandId, ctx, guard)) return result;
      /* The user may have moved to another definition while this loaded */
      const selectedId = get().selectedDefinition?.id;
      if (selectedId != null && selectedId !== definitionId) return result;
      if (result.error) {
        logError('load.snapshots', { definitionId, pageNumber, error: result.error });
        set({ snapshotsError: loadErrorMessage(result.error, 'snapshots') });
        return result;
      }
      const latest = pageNumber === 1 ? (result.data.data[0] ?? null) : undefined;
      set(state => ({
        snapshots: result.data.data,
        snapshotsTotal: result.data.total,
        snapshotsPageNumber: pageNumber,
        snapshotsError: null,
        latestSnapshotByDefinitionId:
          latest === undefined
            ? state.latestSnapshotByDefinitionId
            : { ...state.latestSnapshotByDefinitionId, [definitionId]: latest }
      }));
      if (latest) await get().loadDeliveries(ctx, [latest.id], guard);
      return result;
    },

    setPrompt: prompt => setWizard({ prompt: prompt.slice(0, PROMPT_MAX_LENGTH) }),

    submitPreview: async ctx => {
      const { prompt, preview } = get().wizard;
      if (preview.kind === 'previewing' || !prompt.trim()) return;
      setWizard({ preview: { kind: 'previewing' } });

      const result = await ctx.api.previewDefinition(ctx, { userRequest: prompt });
      if (result.error) {
        setWizard({ preview: previewErrorState(result.error) });
        return;
      }

      setWizard({ previewedPrompt: prompt, preview: previewStateFor(result.data) });
    },

    acceptPreview: () => {
      const { preview } = get().wizard;
      if (preview.kind !== 'definition') return;
      setQuery(flatToTree(pickFilters(preview.preview)), {
        acceptedPreview: preview.preview,
        name: preview.preview.name,
        description: preview.preview.description ?? get().wizard.description,
        count: { kind: 'idle' }
      });
    },

    setWizardName: name => setWizard({ name }),
    setWizardDescription: description => setWizard({ description }),

    setWizardQuery: query => setQuery(query),

    toggleWizardField: name => {
      const { query } = get().wizard;
      if (fieldsInTree(query).has(name)) {
        setQuery(removeFieldRule(query, name));
        return;
      }
      const field = audienceField(name);
      if (field && isOfferedField(field)) setQuery(addFieldRule(query, name));
    },

    updateWizardFilters: patch => setQuery(patchTree(get().wizard.query, patch)),

    setWizardBrandSharePercent: brandSharePercent => {
      const current = get().wizard;
      if (brandSharePercent === current.brandSharePercent) return;
      setWizard({ brandSharePercent, filtersRevision: current.filtersRevision + 1 });
    },

    requestCount: async ctx => {
      const { filters, filtersRevision, dropped, brandSharePercent } = get().wizard;
      if (
        blockingDropped(dropped).length > 0 ||
        hasFilterErrors(validateFilters(filters)) ||
        !isValidBrandShare(brandSharePercent)
      )
        return;
      setWizard({ count: { kind: 'counting', revision: filtersRevision } });

      const result = await ctx.api.countPreview(ctx, {
        ...toRequestFilters(filters),
        brandSharePercent
      });
      /* A newer count has started; let it win */
      const current = get().wizard.count;
      if (current.kind !== 'counting' || current.revision !== filtersRevision) return;
      setWizard({
        count: result.data
          ? { kind: 'counted', memberCount: result.data.memberCount, revision: filtersRevision }
          : { kind: 'error', message: countErrorMessage(result.error), revision: filtersRevision }
      });
    },

    goToDeliverStep: () => {
      if (selectCreateBlocker(get()) != null) return false;
      setWizard({ step: 'deliver' });
      return true;
    },

    goToDefineStep: () => setWizard({ step: 'define' }),

    submitCreate: async (ctx, options = {}) => {
      const state = get();
      const { wizard, targets } = state;
      if (wizard.submitting) return { kind: 'skipped' };
      const blocker =
        selectCreateBlocker(state) ??
        selectTargetsBlocker(state) ??
        (options.createAnyway ? null : selectUnconfirmedCreateBlocker(state));
      if (blocker) return { kind: 'invalid', message: blocker };
      setWizard({ submitting: true, createErrors: [], unconfirmedCreate: null });

      const request = buildCreateRequest({ wizard, targets });
      const submittedAt = Date.now();
      const result = await ctx.api.createDefinition(ctx, request);
      if (result.data) {
        get().resetWizard();
        return {
          kind: 'created',
          definition: result.data,
          adAccountCount: new Set(targets.adAccountIds).size
        };
      }
      if (result.error.status === 400) {
        setWizard({ submitting: false, createErrors: result.error.validationErrors ?? [] });
        return { kind: 'invalid', message: result.error.message };
      }
      /* api.yml: a 409 is a workflows conflict on dispatch, after the rows were written */
      if (result.error.status === 409) {
        get().resetWizard();
        return { kind: 'failedAfterCreate', message: result.error.message };
      }
      /* The failure may have come before or after the insert, so check the list */
      if (UNCONFIRMED_CREATE_STATUSES.includes(result.error.status)) {
        const unconfirmedCreate = await findUnconfirmedCreate(
          ctx,
          request,
          submittedAt,
          UNANSWERED_CREATE_STATUSES.includes(result.error.status)
        );
        setWizard({ submitting: false, unconfirmedCreate });
        return { kind: 'unconfirmed', message: result.error.message };
      }
      setWizard({ submitting: false });
      return { kind: 'failed', message: result.error.message };
    },

    prefillWizardFromDefinition: definition => {
      set({ wizard: INITIAL_WIZARD });
      setQuery(flatToTree(pickFilters(definition)), {
        prompt: definition.nlPrompt ?? '',
        clonedNlPrompt: definition.nlPrompt,
        name: `${definition.name} (copy)`,
        description: definition.description ?? '',
        brandSharePercent: definition.brandSharePercent
      });
    },

    startCloneOf: async (ctx, definitionId) => {
      const { selectedDefinition, definitions } = get();
      const cached =
        selectedDefinition?.id === definitionId
          ? selectedDefinition
          : definitions.find(row => row.id === definitionId);
      const result: ApiResult<AudienceDefinition> = cached
        ? { data: cached }
        : await ctx.api.getDefinition(ctx, definitionId);
      if (result.data) get().prefillWizardFromDefinition(result.data);
      return result;
    },

    resetWizard: () => {
      set({ wizard: INITIAL_WIZARD });
      get().resetTargets();
    }
  };
};

/* Axios codes for a request that never got a response */
const TIMEOUT_CODES = ['ECONNABORTED', 'ETIMEDOUT'];

/**
 * Inline copy and retry advice for a failed preview.
 *
 * - Auth and configuration failures cannot be fixed by retrying.
 * - A timeout, a network drop, and a model or server failure can.
 * - Anything unlisted shows the service's own message and is not retried.
 */
export const previewErrorState = (error: CustomAudiencesApiError): PreviewState => {
  const base = { kind: 'error' as const, validationErrors: error.validationErrors ?? [] };
  const show = (message: string, retryable: boolean): PreviewState => ({
    ...base,
    message,
    retryable
  });
  switch (error.status) {
    case 400:
      return show(error.message, false);
    case 401:
    case 403:
      return show("You don't have permission to preview audiences for this brand.", false);
    case 404:
      return show("Audience preview isn't deployed in this environment yet.", false);
    case 500:
      return show('Something went wrong reading that request. Try again.', true);
    case 502:
      return show("The model couldn't read that request. Try again or reword it.", true);
    /* API Gateway sends 503 when the preview outlasts its integration timeout, and the load balancer sends it when no task is healthy */
    case 503:
      return show('The generator is temporarily unavailable. Please try again.', true);
    case 504:
      return show('Preview timed out. Try again.', true);
    case null:
      if (error.code && TIMEOUT_CODES.includes(error.code))
        return show('Preview timed out. Try again.', true);
      if (error.code === 'ERR_NETWORK')
        return show(
          "Couldn't reach the audiences service. Check your connection and try again.",
          true
        );
      return show(error.message, false);
    default:
      return show(error.message, false);
  }
};

export const countErrorMessage = (error: CustomAudiencesApiError): string =>
  error.status === 504 ? 'Counting timed out. Try a narrower audience.' : error.message;

/* Selectors */

export const selectCountIsStale = (state: Pick<CustomAudiencesStore, 'wizard'>): boolean => {
  const { count, filtersRevision } = state.wizard;
  return count.kind !== 'idle' && count.revision !== filtersRevision;
};

export const selectPromptIsStale = (state: Pick<CustomAudiencesStore, 'wizard'>): boolean =>
  state.wizard.previewedPrompt != null && state.wizard.previewedPrompt !== state.wizard.prompt;

/** Why the definition cannot be created, which gates Next on the Define step; null when it can. */
export const selectCreateBlocker = (state: Pick<CustomAudiencesStore, 'wizard'>): string | null => {
  if (state.wizard.submitting) return 'Creating…';
  if (!state.wizard.name.trim()) return 'Name the audience';
  if (blockingDropped(state.wizard.dropped).length > 0)
    return 'Finish or remove the incomplete rules';
  if (hasFilterErrors(validateFilters(state.wizard.filters))) return 'Fix the filter errors';
  if (!isValidBrandShare(state.wizard.brandSharePercent)) return 'Fix the brand share';
  return null;
};

/**
 * Why a create waits for the user after an unconfirmed one, or null when it need not.
 *
 * - `found` and `unchecked` wait, since the draft may already be saved.
 * - `notFound` does not, since the list loaded without it.
 */
export const selectUnconfirmedCreateBlocker = (
  state: Pick<CustomAudiencesStore, 'wizard'>
): string | null => {
  const kind = state.wizard.unconfirmedCreate?.kind;
  return kind === 'found' || kind === 'unchecked' ? UNCONFIRMED_CREATE_BLOCKER : null;
};

/** Why the Deliver-to selection cannot be submitted, or null when it can. */
export const selectTargetsBlocker = (
  state: Pick<CustomAudiencesStore, 'adAccounts' | 'targets'>
): string | null =>
  selectionBlocker(
    toAdAccountOptions(state.adAccounts),
    state.targets.platform,
    state.targets.adAccountIds
  );

/* One snapshot id per definition, its latest run, for the deliveries load */
const latestSnapshotIds = (snapshots: Record<number, AudienceSnapshot | null>): number[] =>
  Object.values(snapshots).flatMap(snapshot => (snapshot ? [snapshot.id] : []));

const previewStateFor = (response: AudienceDefinitionPreviewResponse): PreviewState => {
  switch (response.outcome) {
    case 'definition':
      return { kind: 'definition', preview: response };
    case 'clarify':
      return { kind: 'clarify', preview: response };
    default:
      return { kind: 'decline', preview: response };
  }
};

/**
 * Looks on the list's first page for the definition a create with `request`, submitted at `submittedAt`, may have saved.
 *
 * - A matching row is `found`.
 * - A failed list load is `unchecked`.
 * - No match is `notFound` when the create was answered, and `unchecked` when `unanswered`, since the row may land after the check.
 */
const findUnconfirmedCreate = async (
  ctx: CustomAudiencesApiContext,
  request: CreateAudienceRequest,
  submittedAt: number,
  unanswered: boolean
): Promise<UnconfirmedCreate> => {
  const [result, userId] = await Promise.all([
    ctx.api.listDefinitions(ctx, { pageSize: DEFINITIONS_PAGE_SIZE, pageNumber: 1 }),
    currentUserId(ctx)
  ]);
  if (result.error) {
    logError('create.unconfirmed', { error: result.error });
    return { kind: 'unchecked' };
  }
  const match = result.data.data.find(row =>
    isUnconfirmedCreateMatch(row, request, submittedAt, userId)
  );
  if (match) return { kind: 'found', definitionId: match.id, name: match.name };
  return unanswered ? { kind: 'unchecked' } : { kind: 'notFound' };
};
